// === IMPORTS ===
import React, { useState, useRef, useEffect } from 'react';
import {
  OfficeRoom,
  RoomType,
  OfficeLayout,
} from '../types';
import { DEFAULT_ROOM_TEMPLATES, RoomTemplate } from '../data/officeCatalog';
import { Plus, Trash2, Layers, Users, ChevronDown } from 'lucide-react';
import { isInsideRoom, findOverlappingRoom } from '../utils/isoMath';
import { createNewOfficeRoom } from '../utils/roomUtils';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { CreateZoneDrawer } from './CreateZoneDrawer';
import AppIcon from './AppIcon';
import './RoomManager.css';

interface RoomManagerProps {
  layout: OfficeLayout;
  onLayoutChange: (updatedLayout: OfficeLayout) => void;
  selectedRoomId: string | null;
  onSelectRoom: (roomId: string | null) => void;
  activeTool: string;
  setActiveTool: (tool: any) => void;
  onOverlapPrevented?: (targetRoomName: string, conflictingRoomName: string) => void;
  roomDraftBounds: {
    col: number;
    row: number;
    width: number;
    height: number;
    color?: string;
    name?: string;
    icon?: string;
  } | null;
  setRoomDraftBounds: (
    bounds: {
      col: number;
      row: number;
      width: number;
      height: number;
      color?: string;
      name?: string;
      icon?: string;
    } | null
  ) => void;
}

const ROOM_TYPE_OPTIONS: { id: RoomType; label: string; icon: string; defaultColor: string }[] = [
  { id: 'work', label: 'Development / Work', icon: 'laptop', defaultColor: '#3b82f6' },
  { id: 'meeting', label: 'Meeting Room', icon: 'users', defaultColor: '#8b5cf6' },
  { id: 'human_interaction', label: 'Human Interaction', icon: 'user-check', defaultColor: '#f97316' },
  { id: 'breakroom', label: 'Cafeteria / Breakroom', icon: 'coffee', defaultColor: '#f59e0b' },
  { id: 'restroom', label: 'Restrooms', icon: 'bath', defaultColor: '#06b6d4' },
  { id: 'server_room', label: 'Servers & DevOps', icon: 'server', defaultColor: '#10b981' },
  { id: 'lounge', label: 'Lounge / Quiet Zone', icon: 'sofa', defaultColor: '#ec4899' },
  { id: 'reception', label: 'Reception / Lobby', icon: 'building', defaultColor: '#6366f1' },
  { id: 'custom', label: 'Custom Zone', icon: 'sparkles', defaultColor: '#14b8a6' },
];

// === COMPONENTE: GESTOR DE ZONAS/ROOMS (Paso 1 del OfficeEditor) ===
export const RoomManager: React.FC<RoomManagerProps> = ({
  layout,
  onLayoutChange,
  selectedRoomId,
  onSelectRoom,
  activeTool,
  setActiveTool,
  onOverlapPrevented,
  roomDraftBounds,
  setRoomDraftBounds,
}) => {
  const tileSize = layout.tileSizeMeters ?? 1.0;
  const rooms = layout.rooms || [];
  const selectedRoom = rooms.find((r) => r.id === selectedRoomId) || null;

  // State for Create Zone Drawer
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState<boolean>(false);
  const [selectedTemplateForDrawer, setSelectedTemplateForDrawer] = useState<RoomTemplate | null>(null);
  const [roomToDelete, setRoomToDelete] = useState<OfficeRoom | null>(null);
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState<boolean>(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);

  // === CERRAR DROPDOWN CON CLIC AFUERA O TECLA ESCAPE ===
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        templateDropdownRef.current &&
        !templateDropdownRef.current.contains(event.target as Node)
      ) {
        setIsTemplateDropdownOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsTemplateDropdownOpen(false);
      }
    };

    if (isTemplateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTemplateDropdownOpen]);

  // Handle Quick Create Room from Template
  const handleQuickCreateRoom = (template: RoomTemplate) => {
    setIsTemplateDropdownOpen(false);
    const result = createNewOfficeRoom({
      template,
      gridWidth: layout.gridWidth,
      gridHeight: layout.gridHeight,
      existingRooms: rooms,
      autoRelocateOnOverlap: true,
    });

    if (!result.success && result.overlapConflict) {
      if (onOverlapPrevented) {
        onOverlapPrevented(result.room.name, result.overlapConflict.name);
      }
      return;
    }

    const updatedRooms = [...rooms, result.room];
    onLayoutChange({
      ...layout,
      rooms: updatedRooms,
    });

    onSelectRoom(result.room.id);
  };

  // Handle Update Room
  const handleUpdateRoom = (updated: OfficeRoom) => {
    const previous = rooms.find((r) => r.id === updated.id);
    if (
      previous &&
      (previous.col !== updated.col ||
        previous.row !== updated.row ||
        previous.width !== updated.width ||
        previous.height !== updated.height)
    ) {
      const conflict = findOverlappingRoom(
        updated,
        rooms.filter((r) => r.id !== updated.id)
      );

      if (conflict) {
        if (onOverlapPrevented) {
          onOverlapPrevented(updated.name, conflict.name);
        }
        return;
      }
    }

    const updatedRooms = rooms.map((r) => (r.id === updated.id ? updated : r));
    onLayoutChange({
      ...layout,
      rooms: updatedRooms,
    });
  };

  // Handle Delete Room
  const handleDeleteRoom = (roomId: string) => {
    const updatedRooms = rooms.filter((r) => r.id !== roomId);
    onLayoutChange({
      ...layout,
      rooms: updatedRooms,
    });
    if (selectedRoomId === roomId) {
      onSelectRoom(null);
    }
  };

  // === RENDER ===
  return (
    <div className="twin-room-manager">
      {/* === PLANTILLAS RÁPIDAS Y BOTÓN AGREGAR === */}
      <div className="twin-room-manager-section">
        <div className="twin-room-manager-section-header">
          <span className="twin-room-manager-section-header-label">
            <Layers className="twin-icon-xs twin-color-theme-accent" />
            <span>Zone Templates</span>
          </span>
          <span className="twin-room-manager-section-count">{DEFAULT_ROOM_TEMPLATES.length} available</span>
        </div>

        <div className="twin-room-manager-template-row" ref={templateDropdownRef}>
          <div className="twin-room-manager-template-trigger-wrap">
            <button
              type="button"
              onClick={() => setIsTemplateDropdownOpen((prev) => !prev)}
              className={`twin-room-manager-template-trigger ${isTemplateDropdownOpen ? 'twin-room-manager-template-trigger--open' : ''}`}
              title="Select zone template for quick creation"
              aria-haspopup="listbox"
              aria-expanded={isTemplateDropdownOpen}
            >
              <div className="twin-room-manager-template-trigger-left">
                <Layers className="twin-icon-xs twin-color-theme-accent" />
                <span className="twin-room-manager-template-trigger-text">Select quick template...</span>
              </div>

              <div className="twin-room-manager-template-trigger-right">
                <span className="twin-room-manager-template-count-badge" title={`${DEFAULT_ROOM_TEMPLATES.length} templates available`}>
                  {DEFAULT_ROOM_TEMPLATES.length}
                </span>
                <ChevronDown className={`twin-icon-xs twin-room-manager-chevron ${isTemplateDropdownOpen ? 'twin-room-manager-chevron--open' : ''}`} />
              </div>
            </button>

            {isTemplateDropdownOpen && (
              <div className="twin-room-manager-template-dropdown">
                <div className="twin-room-manager-template-dropdown-header">
                  <span className="twin-room-manager-template-dropdown-title">Office Templates</span>
                  <span className="twin-room-manager-template-dropdown-hint">1-click build</span>
                </div>

                <div className="twin-room-manager-template-list" role="listbox">
                  {DEFAULT_ROOM_TEMPLATES.map((tmpl) => (
                    <button
                      key={tmpl.name}
                      type="button"
                      role="option"
                      onClick={() => handleQuickCreateRoom(tmpl)}
                      className="twin-room-manager-template-item"
                    >
                      <div className="twin-room-manager-template-item-left">
                        <div
                            className="twin-room-manager-template-item-icon"
                            style={{
                                backgroundColor: `${tmpl.color}15`,
                                borderColor: `${tmpl.color}50`,
                            }}
                            >
                            <span className="twin-room-manager-template-item-icon-color" style={{ color: tmpl.color }}>
                                <AppIcon icon={tmpl.icon} type={tmpl.type} className="twin-icon-xs" />
                            </span>
                            </div>
                        <div className="twin-room-manager-template-item-text">
                          <p className="twin-room-manager-template-item-name">{tmpl.name}</p>
                          <p className="twin-room-manager-template-item-desc">{tmpl.description}</p>
                        </div>
                      </div>

                      <div className="twin-room-manager-template-item-right">
                        <span className="twin-room-manager-template-item-size">
                          {tmpl.defaultWidth}x{tmpl.defaultHeight} ({tmpl.defaultWidth * tmpl.defaultHeight}m²)
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setSelectedTemplateForDrawer(null);
              setIsCreateDrawerOpen(true);
            }}
            title="Build New Custom Zone"
            className="twin-room-manager-add-btn"
          >
            <Plus className="twin-icon-sm" />
          </button>
        </div>
      </div>

      {/* === LISTA DE ZONAS CREADAS === */}
      <div className="twin-room-manager-section">
        <div className="twin-room-manager-section-header">
          <span className="twin-room-manager-section-header-label">
          <Layers className="twin-icon-xs twin-color-theme-accent" />
          <span>Zones on Floor ({rooms.length})</span>
          </span>
        </div>

        {rooms.length === 0 ? (
          <div className="twin-room-manager-empty">
            No zones created yet. Select a quick template or click "+" to build a custom zone.
          </div>
        ) : (
          <div className="twin-room-manager-zones-grid">
            {rooms.map((room) => {
              const isSelected = selectedRoomId === room.id;
              const agentsInside = layout.agents.filter((a) =>
                isInsideRoom(a.currentCol, a.currentRow, room)
              );

              return (
                <div
                  key={room.id}
                  data-drawer-trigger="true"
                  onClick={() => onSelectRoom(isSelected ? null : room.id)}
                  className={`twin-room-manager-zone-card ${isSelected ? 'twin-room-manager-zone-card--selected' : ''}`}
                >
                  <div className="twin-room-manager-zone-badges">
                    {agentsInside.length > 0 && (
                      <span className="twin-room-manager-zone-agent-badge">
                        <Users className="twin-icon-xxs" />
                        <span>{agentsInside.length}</span>
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRoomToDelete(room);
                      }}
                      className="twin-room-manager-zone-delete-btn"
                      title="Delete zone"
                    >
                      <Trash2 className="twin-icon-xs" />
                    </button>
                  </div>

                  <div className="twin-room-manager-zone-icon-wrap">
                    <AppIcon icon={room.icon} name={room.name} type={room.type} className="twin-icon-sm twin-color-theme-accent" />
                    <span className="twin-room-manager-zone-dot" style={{ backgroundColor: room.color || '#3b82f6' }} />
                  </div>

                  <span className="twin-room-manager-zone-name">{room.name}</span>

                  <span className="twin-room-manager-zone-dims">
                    {room.width}x{room.height} ({((room.width * tileSize) * (room.height * tileSize)).toFixed(1)} m²)
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* === DRAWER: CREAR ZONA === */}
      <CreateZoneDrawer
        isOpen={isCreateDrawerOpen}
        onClose={() => setIsCreateDrawerOpen(false)}
        layout={layout}
        onLayoutChange={onLayoutChange}
        onSelectRoom={onSelectRoom}
        onOverlapPrevented={onOverlapPrevented}
        initialTemplate={selectedTemplateForDrawer}
        setRoomDraftBounds={setRoomDraftBounds}
      />

      {/* === MODAL DE CONFIRMACIÓN: ELIMINAR ZONA === */}
      <ConfirmDeleteModal
        isOpen={Boolean(roomToDelete)}
        onClose={() => setRoomToDelete(null)}
        onConfirm={() => {
          if (roomToDelete) {
            handleDeleteRoom(roomToDelete.id);
            setRoomToDelete(null);
          }
        }}
        title="Delete Zone?"
        itemName={roomToDelete?.name}
        itemIcon={roomToDelete?.icon || 'building'}
        itemType="zone"
        description={
          roomToDelete
            ? `Are you sure you want to delete zone "${roomToDelete.name}" (${roomToDelete.width}x${roomToDelete.height} tiles)? Grid space will be freed.`
            : undefined
        }
        warningText="Agents inside this room will be unlinked from this room's policies."
        confirmText="Delete Zone"
      />
    </div>
  );
};